'use client'

import { useEffect } from 'react'

import { useQuery } from '@apollo/client'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  TComboboxProps,
} from '@canyon/ui/Combobox'
import { cn } from '@canyon/ui/utils/cn'

import { ResumesSelect_ResumesDocument } from '@gql/graphql'

interface IResumesSelectProps {
  archived?: boolean
  className?: string
  onValueChange: (value: string) => void
  optimizedResumes?: boolean
  placeholder?: string
  value: string
}

export const ResumesSelect = ({
  archived = false,
  className,
  onValueChange,
  placeholder = 'Select a resume',
  value,
}: IResumesSelectProps) => {
  const { data: resumesData } = useQuery(ResumesSelect_ResumesDocument, {
    variables: { archived },
  })

  const resumes = resumesData?.resumes ?? []

  useEffect(() => {
    if (resumes.length > 0 && !value) {
      onValueChange(resumes[0].id)
    }
  }, [resumes])

  const resumeOptions: TComboboxProps['data'] = resumes.map((resume) => ({
    label: resume.name || 'Unnamed Resume',
    value: resume.id || '',
  }))

  return (
    <Combobox
      data={resumeOptions}
      modal={false}
      onValueChange={onValueChange}
      type="resume"
      value={value}
    >
      <ComboboxTrigger className={cn(className, 'w-full')} />
      <ComboboxContent
        className="z-[60]"
        filter={(value, search) => {
          const item = resumeOptions.find((item) => item.value === value)
          const label = item?.label.toLowerCase() ?? ''
          const searchLower = search.toLowerCase()
          return label.includes(searchLower) ? 1 : 0
        }}
        onWheel={(e) => {
          e.stopPropagation()
        }}
      >
        <ComboboxInput placeholder={placeholder} />
        <ComboboxList
          className="max-h-[200px] overflow-y-auto scroll-smooth"
          onWheel={(e) => {
            e.stopPropagation()
          }}
        >
          <ComboboxEmpty />
          <ComboboxGroup>
            {resumeOptions.map((resume) => (
              <ComboboxItem key={resume.value} value={resume.value}>
                {resume.label}
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
